import type { TobaccoCalcSettings } from "@delta/shared";
// Delta POS for Windows (desktop/; docs/desktop-app.md). When this page runs inside the desktop app, the app's
// sandboxed preload gives it `window.deltaDesktop`: printing on this PC without a separately installed Print Agent and
// without pairing. It answers like the agent's print routes (agent/src/printing/routes.ts serves both), so the
// printing code is the same in a browser and in the app; lib/agent.ts sends print calls here instead of to
// 127.0.0.1. In a browser there is no such object and nothing changes.

export interface DesktopReply { status: number | null; body: unknown; code: string | null }
export interface DesktopInfo {
  app: string;
  version: string;
  platform: string;
  printing: { available: boolean; backend: "windows" | "fake" | "none" | null; problem: string | null; discovery?: { via: "spooler" | "chromium"; problem: string | null } | null };
  oldAgent: { state: "running" | "installed" | "none" | "unknown"; version: string | null };
  /** Since 2 November 2026 (printing diagnostics); older apps leave these out. */
  packaged?: boolean;
  electron?: string;
  os?: string;
  /** The app prints driver-mode pages through Chromium (desktop/src/main/driver-print.ts). */
  driverPrinting?: boolean;
  /** Offline sales are built in (docs/offline-pos.md); older apps leave this out. */
  offlineSales?: boolean;
}
interface Bridge {
  kind: "delta-pos-desktop";
  print(method: string, path: string, body?: unknown): Promise<DesktopReply>;
  info(): Promise<DesktopInfo | null>;
  openSettings(): Promise<void>;
  /** Offline sales (docs/offline-pos.md); apps before 2 November 2026 do not have it. */
  offline?(op: string, body?: unknown): Promise<unknown>;
}

/** The desktop app's bridge, or null in a browser. */
export function desktop(): Bridge | null {
  const d = (window as unknown as { deltaDesktop?: Bridge }).deltaDesktop;
  return d && d.kind === "delta-pos-desktop" && typeof d.print === "function" ? d : null;
}
export const inDesktop = () => desktop() !== null;

/** The print routes the app serves; other agent routes (the training register's) still go to an agent, if any. */
export const isPrintPath = (path: string) => {
  const p = path.split("?")[0]!;
  return p === "/printers" || p.startsWith("/printers/") || p.startsWith("/print/");
};

// ---------------------------------------------------------------- offline sales (docs/offline-pos.md)

export type OfflineRefusal =
  | "not_activated" | "not_provisioned" | "sales_closed" | "grant_expired" | "grant_full" | "clock_moved" | "empty" | "unknown_item" | "blocked_item" | "bad_qty" | "stock"
  | "tender_short" | "bad_tender" | "confirm_total" | "store_failed" | "other_delta";
export interface OfflineStatus {
  activated: boolean; device: { code: string; name: string; origin: string } | null;
  provisioned: boolean; canSell: boolean; why: OfflineRefusal | null;
  grant: { expiresAt: string; maxSales: number; used: number; shiftNumber: string; cashier: string } | null;
  catalog: { version: string; builtAt: string; items: number } | null;
  pending: number; held: number; failed: number; synced: number; oldestPending: string | null;
  lastSyncAt: string | null; lastSyncError: string | null; online: boolean | null; assetsReady: boolean;
}
export interface OfflineUnit { id: string; nameEn: string; nameAr: string; pieces: string; barcodes: string[]; price: string; blocked: string | null }
export interface OfflineItem { id: string; code: string; nameEn: string; nameAr: string; categoryId: string; hasQty: boolean; taxTypeId: string | null; priceIncludesTax: boolean; tobaccoFee: boolean; stock: string | null; units: OfflineUnit[] }
export interface OfflineRegisterData {
  grant: { id: string; issuedAt: string; expiresAt: string; maxSales: number; shiftId: string };
  device: { deviceId: string; code: string; name: string; branchId: string; warehouseId: string; treasuryId: string };
  shift: { id: string; number: string; openedAt: string; treasury: { id: string; nameEn: string; nameAr: string } };
  cashier: { id: string; name: string };
  organization: { id: string; nameEn: string; nameAr: string };
  seller: { nameEn: string; nameAr: string; taxNumber: string; address: string; legalInvoiceSystem: string; logo: string | null; logoDataUrl: string | null };
  catalog: { version: string; builtAt: string; warehouseId: string; items: OfflineItem[]; categories: { id: string; nameEn: string; nameAr: string }[]; taxTypes: { id: string; nameEn: string; nameAr: string; rate: string }[];
    tobacco: TobaccoCalcSettings };
  allowed: Record<string, boolean>;
  origin: string;
  testDocument: boolean;
}
export interface OfflineSale {
  saleId: string; deviceSeq: number; receiptNo: string; createdAt: string; deviceTime: string; payload: string; state: "pending" | "synced" | "held" | "failed";
  invoiceCode: string | null; heldBy: string | null; lastError: string | null; attempts: number; syncedAt: string | null; prints: number;
}
export interface OfflinePayload {
  version: 2; mode: "sale"; shiftId: string; cashierId: string; warehouseId: string; receiptNo: string; catalogVersion: string;
  lines: { itemId: string; itemUnitId: string; qty: string; price: string; taxTypeId: string }[];
  payments: { treasuryId: string; amount: string }[]; cashTendered: string | null; confirmTobaccoTotal: string | null; totals: { grand: string; vat: string; fee: string };
}
export type OfflineSellResult =
  | { ok: true; sale: OfflineSale; payload: OfflinePayload; change: string | null }
  | { ok: false; reason: OfflineRefusal; detail?: string; revisedTotal?: string; lineNo?: number };

/** Calls the app's offline door, or null outside the app (or in an app without offline sales). */
export async function offlineCall<T>(op: string, body?: unknown): Promise<T | null> {
  const d = desktop();
  if (!d?.offline) return null;
  try { return (await d.offline(op, body ?? {})) as T; } catch { return null; }
}
export const hasOffline = () => !!desktop()?.offline;
/** The offline register runs from the copy of Delta kept in the app, not from Delta's site. */
export const onOfflineOrigin = () => location.protocol === "delta-offline:";
